import { Split } from 'lucide-react';
import type { BoolExpr, LBNodeData } from '@/proto/logiblocks/logiblocks';
import { LogicalOp } from '@/proto/logiblocks/logiblocks';
import { formatVariableRef } from '@/pages/Logiblocks/variables/variableUtils';
import {
  formatUtcSecondsAsUserTime,
  utcSecondsToUserSeconds,
} from '../../dateTimeUtils';
import { getUserTimeZone } from '@/common/utils/utils';
import { CONTROL_FLOW_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { expressionOpLabel, getExprLhs, getExprOp } from './expressionOp';
import { LOGICAL_OP_LABELS } from '../../enumData';
import { ConditionalCustomization } from './ConditionalCustomization';
import styles from './ConditionalNodeInfo.module.css';
import { ConditionalRunStepDetail } from './ConditionalRunStepDetail';
import { getConditionalRunStepSummary } from './conditionalRunStepSummary';
import { buildNodeData } from '../../nodeDataUtils';
import {
  formatLeaf,
  validateDelayEvents,
  validateGroupRefs,
  validateGroupStructure,
  validateOperatorCatalog,
} from './conditionalNodeUtils';

// Find the first leaf expression, recursing into nested groups.
function findFirstLeaf(expr: BoolExpr): BoolExpr | undefined {
  if (!expr.group) {
    return expr;
  }
  const first = expr.group.expressions[0];
  return first ? findFirstLeaf(first) : undefined;
}

export const ConditionalNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'conditional',

  category: NodeCategory.CONTROL_FLOW,

  label: 'Path',

  hasInput: true,

  getIcon: (size: number) => (
    <Split size={size} color={CONTROL_FLOW_ICON_COLOR} />
  ),

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, graph: GraphData) => {
    const root = data.conditional?.root;
    if (!root || root.expressions.length === 0) {
      return 'Always passes';
    }

    const { errors } = validateGroupStructure(root, 0, true);
    if (errors.length > 0) {
      return '(error!)';
    }

    const first = findFirstLeaf(root.expressions[0]);
    if (first == null) {
      return '…';
    }

    const label = formatLeaf(first, graph.nodes);
    if (root.expressions.length === 1) {
      const rw = first.recurring_window_expr;
      const tz = getUserTimeZone().name;
      const isOvernightLocally =
        rw &&
        (rw.start_seconds != null || rw.end_seconds != null) &&
        utcSecondsToUserSeconds(rw.end_seconds ?? 0, tz) <
          utcSecondsToUserSeconds(rw.start_seconds ?? 0, tz);
      if (isOvernightLocally) {
        const lhs = getExprLhs(first);
        const op = getExprOp(first);
        const lhsLabel = lhs
          ? formatVariableRef(lhs, graph.nodes, 'short')
          : '?';
        const opLabel = op ? expressionOpLabel(op) : '?';
        const start = formatUtcSecondsAsUserTime(rw.start_seconds ?? 0);
        const end = formatUtcSecondsAsUserTime(rw.end_seconds ?? 0);
        return (
          <>
            {lhsLabel} {opLabel} {start} –{' '}
            <span className={styles.overnightEndTime}>
              {end}
              <sup className={styles.nextDayMark}>+1</sup>
            </span>
          </>
        );
      }
      return label;
    }
    const joinOp = LOGICAL_OP_LABELS[root.operators[0] ?? LogicalOp.AND];
    return `${label} ${joinOp} …`;
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const root = nodeData.conditional?.root;
    if (!root) {
      return undefined;
    }
    const { errors: structErrors } = validateGroupStructure(root, 0, true);
    const refErrors = validateGroupRefs(root, 0, nodeId, graph).errors;

    // Operator-vs-type validation runs on every conditional. Each leaf's type
    // is resolved from its variable ref, which points at the ancestor node
    // that produces the variable; leaves whose type can't be resolved are
    // skipped, mirroring the backend operator-catalog check.
    const { errors: operatorErrors } = validateOperatorCatalog(root, 0, graph);
    const { errors: delayEventErrors } = validateDelayEvents(root, 0, graph);

    const errors = [
      ...structErrors,
      ...refErrors,
      ...operatorErrors,
      ...delayEventErrors,
    ];
    return errors.length > 0 ? errors : undefined;
  },

  getRunStepSummary: getConditionalRunStepSummary,

  RunStepDetailComponent: ConditionalRunStepDetail,

  CustomizationComponent: ConditionalCustomization,

  createDefaultNodeData: () =>
    buildNodeData('conditional', {
      root: { expressions: [], operators: [] },
    }),
};
